import { createRef, Fragment, Component } from 'react';
import { connect } from 'react-redux';
import { Button, Checkbox, IconButton } from '@upmc/ims.component-library';
import * as actions from '../../../../store/apps/actions';
import classes from './Multiselect.module.scss';
import Icon from '../../../../components/UI/Icon/Icon';
import ChipSet from '../../../../components/UI/Chips/ChipSet/ChipSet';
import Chip from '../../../../components/UI/Chips/Chip/Chip';
import List, {
	ListItem,
	MDCListItemText,
	MDCListDivider,
	MDCListItemMeta,
} from '../../../../components/UI/MDC/List/List';
import TextField from '../../../../components/UI/MDC/TextField/TextField';
import PopupIconButton from '../../../../components/UI/Input/PopupIconButton/PopupIconButton';
import { Headline3 } from '../../../../components/UI/MDC/Typography/Typography';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classnames from 'classnames';
import WithLoadingHandler from '../../../../hoc/withLoadingHandler/withLoadingHandler';
import { formatForID } from '../../../../utils/strings/StringUtil';

class Multiselect extends Component {
	/**
	 * Checks if @param obj contains property with name @param prop
	 * @param {Object} obj object to check in
	 * @param {String} prop property name to check for
	 * @returns {Object|String} nullable value of property in object
	 * @example
	 * // returns true if obj of structure {multiselect: {isSearch: {true}...}}...
	 * getProp(obj, "isSearch")
	 */
	getProp = (obj, prop) => {
		if (obj !== null) {
			if (obj.hasOwnProperty(prop)) {
				return obj[prop];
			}
		}

		return null;
	};

	hasExtras =
		this.getProp(this.props, 'extras') !== null &&
		this.getProp(this.props.extras, 'multiselect') !== null;
	isReadOnly = this.hasExtras
		? this.getProp(this.props.extras.multiselect, 'isReadOnly') === 'true'
		: null;
	isSearch =
		this.hasExtras &&
		this.getProp(this.props.extras.multiselect, 'isSearch') !== null
			? this.props.extras.multiselect.isSearch === 'true'
			: false;
	searchRoute =
		this.hasExtras &&
		this.getProp(this.props.extras.multiselect, 'searchRoute') !== null
			? this.props.extras.multiselect.searchRoute
			: null;
	isOverridable = this.isSearch
		? this.getProp(this.props.extras.multiselect, 'isOverridable') ===
		  'true'
		: null;
	hasSelectAll =
		this.hasExtras &&
		this.getProp(this.props.extras.multiselect, 'hasSelectAll') !== null
			? this.props.extras.multiselect.hasSelectAll === 'true'
			: true;
	hasFilter =
		this.hasExtras &&
		this.getProp(this.props.extras.multiselect, 'hasFilter') !== null
			? this.props.extras.multiselect.hasFilter === 'true'
			: true;
	trailingIcon = this.hasExtras
		? this.getProp(this.props.extras.multiselect, 'trailingIcon')
		: null;
	maxCount =
		this.hasExtras &&
		this.getProp(this.props.extras, 'rules') !== null &&
		this.getProp(this.props.extras.rules, 'maxCount') !== null &&
		this.getProp(this.props.extras.rules.maxCount, 'value') !== null
			? parseInt(this.props.extras.rules.maxCount.value)
			: null;
	label = this.hasExtras
		? this.getProp(this.props.extras.multiselect, 'label')
		: null;

	constructor(props) {
		super(props);
		this.refMultiSelect = createRef();
		this.isReadOnly = this.isReadOnly === null ? false : this.isReadOnly;
		this.isSearch = this.isSearch === null ? false : this.isSearch;
	}

	/**
	 * Compares @param arrDep against @param arrInde to determine if @param arrInde contains all of unsorted @param arrDep
	 * @param {Object[]} arrDep dependent variable in comparison
	 * @param {Object[]} arrInde independent variable in comparison; the array to compare against
	 * @returns {boolean} Indicates if independent array contains all of unsorted, dependent array
	 * @example
	 * // returns false
	 * compareArrs([{Key: "1", Value: "value"}], [{Key: "10", Value: "value"}])
	 * // returns true
	 * compareArrs([{Key: "1", Value: "value"}], [{Key: "10", Value: "value"}, {Key: "1", Value: "value"}])
	 */
	compareArrs = (arrDep, arrInde) => {
		if (arrDep.length > 0 && arrDep.length <= arrInde.length) {
			return arrDep.every(el => this.checkExistence(arrInde, el));
		}
		return false;
	};

	/**
	 * Checks existence of @param obj property "Value" in @param arr
	 * . Assumes @param arr contains instance of and @param obj is type of object with property "Value".
	 * @param {Object[]} arr Array to check existence within
	 * @param {Object} obj Object to check existence of
	 * @returns {boolean} Indicates if array contains an object with property "Value" equal to object instance property "Value"
	 * @example
	 * // returns false
	 * checkExistence([{Key: "0", Value: "1"}], {Key: "1", Value: "10"})
	 * // returns true
	 * checkExistence([{Key: "0", Value: "1"}], {Key: "1", Value: "1"})
	 */
	checkExistence = (arr, obj) => {
		return arr.find(el => el.Value === obj.Value) ? true : false;
	};

	/**
	 * Calculates index of component's form in array / object of forms based on app ID.
	 * Type of forms object changes from @type {array} to @type {object}, must be accounted for in logic.
	 * @returns {number} Index of component's form in array / object of forms.
	 */
	getFormId = () =>
		Array.isArray(this.props.allForms)
			? this.props.allForms.findIndex(app => app.id === this.props.appId)
			: Object.keys(this.props.allForms).findIndex(
					key => this.props.allForms[key].id === this.props.appId
			  );

	/**
	 * Retrieves the index of object with property "Value" equal to @param value in array @param arr
	 * @param {Object[]} arr Arrary to check index in
	 * @param {string} value Value to check index of
	 * @returns {number} Index of object in array instance with property "Value" of string instance.
	 * @example
	 * // returns -1
	 * getIndexOf([{Key: "0", Value: "1"}, {Key: "1", Value: "10"}], "100")
	 * // return 1
	 * getIndexOf([{Key: "0", Value: "1"}, {Key: "1", Value: "10"}], "10")
	 */
	getIndexOf = (arr, value) => {
		const obj = arr.find(el => el.Value === value);
		return arr.indexOf(obj);
	};

	/**
	 * Retrieves array of data assigned to component via props; conditionally applies filtering to array.
	 * Results in list of elements for multiselect being referenced from props, not state.
	 * @returns {Object[]} Array of objects with properties "Key", "Value"
	 */
	getListItems = () => {
		const listItems =
			this.props.allForms[this.getFormId()].model.members[this.props.name]
				.elementConfig.options[
				this.props.allForms[this.getFormId()].model.members[
					this.props.name
				].id
			].ApplicationData;

		return this.state.filterOn !== ''
			? listItems.filter(el =>
					new RegExp(this.state.filterOn + '+', 'i').test(el.Value)
			  )
			: listItems;
	};

	/**
	 * Retrieves length of array of data assigned to component.
	 * @returns {number} Size of array; default 0.
	 */
	getListLength = () => {
		const listItems = this.getListItems();
		return listItems !== null ? listItems.length : 0;
	};

	state = {
		filterOn: '', // filter to apply to array of data assigned to component
		searchOn: '',
		selectedIndex: -1,
		hasListRendered: false,
		isFocused: false, // manages "focused" styling of component
		isOpen: false, // manages collapsable drawer of component
		isOverride: false,
	};

	refTextFieldOverride = createRef();
	refTextFieldSearch = createRef(); // necessary for retrieving value of textField

	handleCheckChip = itemSelected => {
		this.handleCheck(itemSelected);
	};

	handleExpander = () => {
		let toggle = !this.state.isOpen;
		this.setState({ isOpen: toggle });
	};

	/**
	 * Raises parent onChangeHandler for each change selection.
	 * @param {Object[]} arrSelections array of objects with properties "Key", "Value".
	 * The array of changed selections, not the entire array of selections.
	 * @param {boolean} isAdd indicates whether to add or remove each changed selection.
	 */
	raiseChangeHandler = (arrSelections, isAdd) => {
		const isEmpty = !(arrSelections.length > 0);

		let arrSelectionsCopy = !isEmpty
			? [...arrSelections]
			: this.props.value; // accounts for "Deselect all"

		arrSelectionsCopy.forEach(el => {
			const event = {
				target: {
					checked: isAdd,
					id: el.Value,
					name: this.props.name,
					options: this.props.elementConfig.options,
					selectedIndex: this.getIndexOf(
						this.getListItems(),
						el.Value
					),
					value: el.Value,
				},
			};
			this.props.changeHandler(event, true);
		});
		this.setState({ listSelected: arrSelections });
	};

	handleCheck = itemSelected => {
		let arrSelection = [itemSelected];
		let isAdd = false;
		if (this.checkExistence(this.props.value, itemSelected)) {
			//unchecked = remove
			if (this.refMultiSelect) {
				this.refMultiSelect.current.focus(); // pass focus from hidden chip (or) chip set to visible multi-select <div>
			}
		} else {
			//checked = insert
			isAdd = true;
		}

		this.raiseChangeHandler(arrSelection, isAdd);
	};

	handleCheckAll = e => {
		let isAdd = false;
		if (e.target) {
			let arrSelections = this.props.value;
			if (e.target.checked) {
				// select all remaining
				this.getListItems().map(el =>
					!this.checkExistence(arrSelections, el)
						? arrSelections.push(el)
						: arrSelections.splice(el, 1)
				);
				isAdd = true;
			} else {
				// unselect all
				arrSelections = [];
			}
			this.raiseChangeHandler(arrSelections, isAdd);
		}
	};

	/**
	 *  Manually handle indeterminate checkbox clicks for cross-browser support. Handler only called when checkbox in "indeterminate" state.
	 */
	handleIndeterminateCheck = () => {
		let e = { target: { checked: true } };
		this.handleCheckAll(e);
	};

	handleTextFieldFilterChange = e => {
		let value = e.target.value.trim();
		this.setState({ filterOn: value !== null ? value : '' });
	};

	handleTextFieldOverrideChange = e => {
		let value = e.target.value.trim();
		if (value.length > 0) {
			let obj = { Key: value, Value: value };
			if (!this.checkExistence(this.props.value, obj)) {
				this.raiseChangeHandler([obj], true);
				this.setState({ isOverride: false });
				this.refTextFieldOverride.current.setValue('');
			}
		}
	};

	handleTextFieldSearchChange = e => {
		let value = String(e.target.value).trim();
		value = value.length > 0 ? value : 'undefined';

		let members = {};
		members['appID'] = {
			elementConfig: {},
			value: this.props.allForms[this.getFormId()].id,
		};

		const idComponent = this.props.name;
		members[idComponent] = {
			...this.props.allForms[this.getFormId()].model.members[idComponent],
		};

		members[idComponent].populationRoute = this.searchRoute;
		members['input'] = {
			elementConfig: {},
			value: value,
		};

		this.props.onTextFieldSearchHandler({
			allMembers: members,
			trackingNumber: this.props.trackingNumber,
			formToPopulateKey: this.getFormId(),
			watchComponentKey: this.props.name,
		});

		this.setState({ searchOn: value === 'undefined' ? '' : value });
	};

	onBlurHandler = () => this.setState({ isFocused: false });
	onFocusHandler = () => this.setState({ isFocused: true });
	onOverrideClick = () => this.setState({ isOverride: true });

	/**
	 * Lifecycle method executed when component renders to DOM.
	 * Opens the component if the component has a pre-existing value on mount.
	 * @returns void
	 */
	componentDidMount() {
		if (this.props.value && this.props.value.length) {
			this.setState({ isOpen: true });
		}
	}

	/**
	 * Lifecycle method invoked after update to component.
	 * Confirms the list has rendered after the component has opened before action taken on list items.
	 * Used to render the list without error and allow for list items to be disabled (specifically the first list item) when maxCount has been reached.
	 * @see Multiselect.maxCount
	 * @see componentDidMount for additional context.
	 * @see `node_modules/@material/list/dist/mdc.list.js`, `hasCheckboxAtIndex()` method for source of error.
	 * @see `node_modules/@material/list/constants.js`, `CHECKBOX_SELECTOR` property for explanation of error.
	 * @param {Object} _ Throwaway parameter.
	 * @param {Object} prevState Local state prior to update.
	 * @returns void
	 */
	componentDidUpdate(_, prevState) {
		let hasListRendered = this.state.isOpen && this.getListLength();
		if (prevState.hasListRendered !== hasListRendered) {
			this.setState({ hasListRendered: hasListRendered });
		}
	}

	render() {
		const isListSelected = this.compareArrs(
			this.getListItems(),
			this.props.value
		); // if entire array of data in view is selected

		const isListIndeterminate =
			this.props.value.some(selection =>
				this.checkExistence(this.getListItems(), selection)
			) && !isListSelected; // if some of array of data in view is selected

		const SELECT_ALL_TEXT = isListSelected ? 'Deselect All' : 'Select All';

		let propsFocus = {
			onBlur: () => this.onBlurHandler(),
			onFocus: () => this.onFocusHandler(),
		};

		let classesMultiSelect = classnames({
			[`${classes['multi-select']}`]: true,
			[`${classes['multi-select--focused']}`]: this.state.isFocused,
		});

		let textFieldHelp =
			this.getListLength() > 0
				? null
				: this.isSearch
				? this.state.searchOn !== ''
					? 'Increase search specificity.'
					: null
				: 'No elements.';

		let styleLabel =
			this.props.value.length > 0 ? { display: 'none' } : null;

		let styleToolBar = null;
		let styleTextFieldSearch = null;
		let isTextFieldSearchFullWidth = false;
		let checkboxFilter = null;
		let textFieldFilter = null;

		if (
			this.isSearch &&
			this.state.searchOn === '' &&
			this.getListLength() === 0 &&
			this.state.filterOn === ''
		) {
			styleToolBar = classes['multi-select__tool-bar--empty'];
			styleTextFieldSearch = classes['multi-select__text-field--flex'];
			isTextFieldSearchFullWidth = true;
		} else {
			styleToolBar = [
				classes['multi-select__tool-bar--filled'],
				'mdc-form-field',
			].join(' ');

			styleTextFieldSearch = this.hasSelectAll
				? classes['multi-select__text-field--block']
				: classes['multi-select__text-field--block--leading'];

			checkboxFilter = (
				<Checkbox
					title={SELECT_ALL_TEXT}
					aria-label={SELECT_ALL_TEXT}
					checked={isListSelected}
					inputProps={{
						id: `app-${this.props.appId}__component-${parseInt(
							this.props.name
						)}___select-all`,
					}}
					disabled={this.getListLength() === 0 || this.isReadOnly}
					indeterminate={isListIndeterminate}
					label={!this.hasSelectAll ? null : SELECT_ALL_TEXT}
					onChange={e => this.handleCheckAll(e)}
					onClick={
						isListIndeterminate
							? () => this.handleIndeterminateCheck()
							: null
					}
					style={!this.hasSelectAll ? { display: 'none' } : null}
					{...propsFocus}
				/>
			);

			textFieldFilter = (
				<TextField
					canClear
					className={
						this.hasSelectAll
							? classes['multi-select__text-field--block']
							: classes[
									'multi-select__text-field--block--leading'
							  ]
					}
					inputProps={{
						id: `app-${this.props.appId}__component-${parseInt(
							this.props.name
						)}__filter`,
					}}
					help={textFieldHelp}
					label='Filter'
					style={!this.hasFilter ? { display: 'none' } : null}
					leadingIcon={<Icon icon='filter_list' />}
					onChange={e => this.handleTextFieldFilterChange(e)}
					{...propsFocus}
				/>
			);
		}
		let textFieldSearch = this.isSearch ? (
			<TextField
				className={styleTextFieldSearch}
				fullWidth={isTextFieldSearchFullWidth}
				help={textFieldHelp}
				inputProps={{
					id: `app-${this.props.appId}__component-${parseInt(
						this.props.name
					)}__search`,
					placeholder: 'Search for elements',
					onKeyPress: e =>
						e.key === 'Enter'
							? this.handleTextFieldSearchChange(e)
							: null,
				}}
				trailingIcon={<Icon icon='search' />}
				onTrailingIconSelect={() => {
					let event = {
						target: {
							value: this.refTextFieldSearch.current.getValue(),
						},
					};
					this.handleTextFieldSearchChange(event);
				}}
				//  onChange={(e) => this.handleTextFieldSearchChange(e)}
				{...propsFocus}
				ref={this.refTextFieldSearch}
			/>
		) : null;

		let textField = this.isSearch ? textFieldSearch : textFieldFilter;

		let buttonOverride =
			this.isOverridable && this.state.searchOn ? (
				<Button
					onClick={() => this.onOverrideClick()}
					variant='text'
					id={`app-${this.props.appId}__component-${parseInt(
						this.props.name
					)}__button-override`}
					sx={this.state.isOverride ? { display: 'none' } : {}}
				>
					Element Not Found?
				</Button>
			) : null;

		let textFieldOverride =
			buttonOverride !== null ? (
				<TextField
					className={styleTextFieldSearch}
					fullWidth={isTextFieldSearchFullWidth}
					help={''}
					inputProps={{
						id: `app-${this.props.appId}__component-${parseInt(
							this.props.name
						)}__text-field-override`,
						placeholder: 'Manually add element',
						onKeyPress: e =>
							e.key === 'Enter'
								? this.handleTextFieldOverrideChange(e)
								: null,
					}}
					trailingIcon={
						<Icon
							icon='add'
							id={`app-${this.props.appId}__component-${parseInt(
								this.props.name
							)}__text-field-override__add-icon`}
						/>
					}
					onTrailingIconSelect={() => {
						let event = {
							target: {
								value: this.refTextFieldOverride.current.getValue(),
							},
						};
						this.handleTextFieldOverrideChange(event);
					}}
					{...propsFocus}
					style={this.state.isOverride ? {} : { display: 'none' }}
					ref={this.refTextFieldOverride}
				/>
			) : null;

		let toolBarContent = (
			<Fragment>
				{checkboxFilter}
				{textField}
				{textFieldOverride}
				{buttonOverride}
			</Fragment>
		);

		let toolBar = (
			<StyleAuxiliary
				classes={
					!this.hasSelectAll && !this.hasFilter && !this.isSearch
						? null
						: styleToolBar
				}
			>
				{toolBarContent}
			</StyleAuxiliary>
		);

		let listItems = this.getListItems();

		let list =
			this.getListLength() > 0 ? (
				<StyleAuxiliary classes={classes['multi-select__list']}>
					<List
						xlarge
						checkboxList={this.isReadOnly ? false : true}
						singleSelection={this.isReadOnly ? true : false}
						selectedIndex={
							this.isReadOnly
								? this.state.selectedIndex
								: this.props.value.map(item =>
										this.getIndexOf(
											this.getListItems(),
											item.Value
										)
								  )
						}
						handleSelect={indexSelected =>
							this.isReadOnly
								? this.setState({
										selectedIndex: indexSelected,
								  })
								: this.handleCheck(
										this.getListItems()[indexSelected]
								  )
						}
						name={this.props.name}
						{...propsFocus}
					>
						{listItems.map((el, index) => {
							//enable or disable the divider
							let divider = <MDCListDivider tag='div' />;
							if (index === listItems.length - 1) {
								divider = null;
							}
							//check or uncheck checkbox
							const selectedItems = this.props.value;
							const isChecked = this.checkExistence(
								selectedItems,
								el
							);
							let checkbox = isChecked ? (
								<Checkbox
									checked={isChecked}
									{...propsFocus}
									inputProps={{
										id: `app-${
											this.props.appId
										}__component-${parseInt(
											this.props.name
										)}__option-${formatForID(el.Value)}`,
									}}
								/>
							) : this.isReadOnly ||
							  (this.maxCount &&
									this.maxCount === this.props.value.length &&
									!this.props.value.includes(el.Value) &&
									this.state.hasListRendered) ? (
								<Checkbox
									disabled
									{...propsFocus}
									inputProps={{
										id: `app-${
											this.props.appId
										}__component-${parseInt(
											this.props.name
										)}__option-${formatForID(el.Value)}`,
									}}
								/>
							) : (
								<Checkbox
									checked={isChecked}
									{...propsFocus}
									inputProps={{
										id: `app-${
											this.props.appId
										}__component-${parseInt(
											this.props.name
										)}__option-${formatForID(el.Value)}`,
									}}
								/>
							);
							let itemMeta =
								this.trailingIcon !== null ? (
									<MDCListItemMeta
										meta={
											<PopupIconButton
												{...this.trailingIcon}
												appId={this.props.appId}
												id={`app-${
													this.props.appId
												}__component-${parseInt(
													this.props.name
												)}__option-${formatForID(
													el.Value
												)}__trailing-icon`}
												value={el.Value}
											/>
										}
									/>
								) : null;
							//return the checkbox and label

							return (
								<div key={el.Key}>
									<ListItem {...propsFocus}>
										{checkbox}
										<MDCListItemText
											primaryText={el.Value}
										/>
										{itemMeta}
									</ListItem>
									{divider}
								</div>
							);
						})}
					</List>
				</StyleAuxiliary>
			) : null;

		let content = this.state.isOpen ? (
			<Fragment>
				{toolBar}
				{list}
			</Fragment>
		) : null;

		const chips = this.props.value.map((item, index) => (
			<Chip
				id={Number(index) + 1}
				key={item.Key}
				label={item.Value}
				trailingIcon={
					<Icon
						icon='delete'
						id={`app-${this.props.appId}__component-${parseInt(
							this.props.name
						)}__option-${formatForID(item.Value)}__chip__remove`}
					/>
				}
				handleTrailingIconInteraction={() => this.handleCheckChip(item)}
				{...propsFocus}
			/>
		));

		return (
			<Fragment>
				<WithLoadingHandler>
					<div
						className={classesMultiSelect}
						tabIndex='0'
						{...propsFocus}
						ref={this.refMultiSelect}
					>
						<Headline3
							className={classes['multi-select__label']}
							style={styleLabel}
							{...propsFocus}
						>
							{this.props.label ? this.props.label : this.label}
						</Headline3>
						<div
							className={classes['multi-select__chip-set']}
							{...propsFocus}
						>
							<ChipSet {...propsFocus}>{chips}</ChipSet>
						</div>
						<div
							className={classes['multi-select__toggle']}
							{...propsFocus}
						>
							<IconButton
								onClick={() => this.handleExpander()}
								{...propsFocus}
								title='Toggle Multi-Select'
								aria-label='Toggle Multi-Select'
								id={`app-${
									this.props.appId
								}__component-${parseInt(
									this.props.name
								)}__expand`}
							>
								{this.state.isOpen ? (
									<Icon icon='expand_less' />
								) : (
									<Icon icon='expand_more' />
								)}
							</IconButton>
						</div>
					</div>
					{content}
				</WithLoadingHandler>
			</Fragment>
		);
	}
}

const mapStateToProps = (state, ownProps) => {
	const formIndex = Object.keys(state.apps.allForms).find(
		formIndex => state.apps.allForms[formIndex].id === ownProps.appId
	);
	const model = state.apps.allForms[formIndex].model;
	let value = model.members[ownProps.name]
		? model.members[ownProps.name].value
		: [];
	value =
		value !== null && value.length > 0
			? value.split('~~').map(el => {
					return { Key: el, Value: el };
			  })
			: [];
	return {
		allForms: state.apps.allForms,
		trackingNumber: state.apps.trackingNumber,
		value: value,
	};
};

const mapDispatchToProps = dispatch => {
	return {
		onTextFieldSearchHandler: memberInfo =>
			dispatch(actions.callApiPopulate(memberInfo)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Multiselect);
